import React, { type ComponentType, Suspense, useMemo } from 'react';

import type { PreloadedQuery } from 'react-relay';

import type { ExtensionHandlers, ExtensionParams } from '@atlaskit/editor-common/extensions';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { ArticleSearchComponent, HelpObjectSearchComponent } from '@atlassian/article-search';
import {
	ExtensionMode,
	type ProductDependenciesProviderProps,
	RESOURCE_TILES_EXTENSION_TYPE,
} from '@atlassian/company-hub-common';
import {
	HUB_CONNECT_EXTENSION_TYPE,
	type HubConnectExtensionParameters,
} from '@atlassian/hub-connect-extension';
import {
	HUB_FORGE_EXTENSION_TYPE,
	type HubForgeExtensionParameters,
} from '@atlassian/hub-forge-extension';
import { JSM_HELP_EXTENSION_TYPE, type JSMHelpParameters } from '@atlassian/jsm-help-extension';
import { type ResourceTilesParameters } from '@atlassian/knowledge-cards-extension';
import {
	CALLOUT_BANNER_EXTENSION_TYPE,
	type CalloutBannerParametersType,
} from '@atlassian/page-topper-extension';
import type { rovoWidgetProviderQuery } from '@atlassian/rovo-service-agent';

export interface ProductExtensionHandlersProps {
	contentId: string;
	product: string;
	PropsProvider: ComponentType<ProductDependenciesProviderProps>;
	/**
	 * Rovo widget query preloaded at the hub level so the request runs in parallel
	 * with the callout banner's lazy chunk download. Forwarded to the callout
	 * banner extension handler.
	 */
	rovoWidgetQueryReference?: PreloadedQuery<rovoWidgetProviderQuery> | null;
}

const CalloutBannerExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_page-topper-extension" */ '@atlassian/page-topper-extension'
	).then((mod) => ({ default: mod.CalloutBannerExtension })),
);

const ResourceTilesExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_knowledge-cards-extension" */ '@atlassian/knowledge-cards-extension'
	).then((mod) => ({ default: mod.ResourceTilesExtension })),
);

const JSMHelpExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_jsm-help-extension" */ '@atlassian/jsm-help-extension'
	).then((mod) => ({ default: mod.JSMHelpExtension })),
);

const HubForgeExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_hub-forge-extension" */ '@atlassian/hub-forge-extension'
	).then((mod) => ({ default: mod.HubForgeExtension })),
);

const HubConnectExtensionLazy = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_hub-connect-extension" */ '@atlassian/hub-connect-extension'
	).then((mod) => ({ default: mod.HubConnectExtension })),
);

/**
 * Hub Forge / Connect extension handlers are gated by `forge-on-hubs` and
 * only registered for Help Hub / JSM. CSM does not support these extension
 * types.
 */
const getHubForgeAndConnectExtensionHandlers = (
	contentId: string,
	PropsProvider: ComponentType<ProductDependenciesProviderProps>,
): ExtensionHandlers => {
	if (!fg('forge-on-hubs')) {
		return {};
	}
	const handlers: ExtensionHandlers = {};
	const defaultExtensionHandlerProps = {
		contentId,
		propsProvider: PropsProvider,
		mode: ExtensionMode.RENDERER,
	};
	handlers[HUB_FORGE_EXTENSION_TYPE] = (
		extension: ExtensionParams<HubForgeExtensionParameters>,
	) => (
		<Suspense fallback={<UFOLoadHold name="hub-forge-extension-loader" />}>
			<HubForgeExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>
	);
	handlers[HUB_CONNECT_EXTENSION_TYPE] = (
		extension: ExtensionParams<HubConnectExtensionParameters>,
	) => (
		<Suspense fallback={<UFOLoadHold name="hub-connect-extension-loader" />}>
			<HubConnectExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>
	);
	return handlers;
};

const getCSMExtensionHandlers = (
	contentId: string,
	PropsProvider: ComponentType<ProductDependenciesProviderProps>,
): ExtensionHandlers => {
	const handlers: ExtensionHandlers = {};

	if (!contentId) {
		return handlers;
	}

	const defaultExtensionHandlerProps = {
		contentId,
		propsProvider: PropsProvider,
		mode: ExtensionMode.RENDERER,
		CustomSearchComponent: ArticleSearchComponent,
	};

	// Callout banner Extension Handler
	handlers[CALLOUT_BANNER_EXTENSION_TYPE] = (
		extension: ExtensionParams<CalloutBannerParametersType>,
	) => (
		<Suspense fallback={<UFOLoadHold name="page-topper-extension-loader" />}>
			<CalloutBannerExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>
	);

	// Resource Tiles Extension Handler
	handlers[RESOURCE_TILES_EXTENSION_TYPE] = (
		extension: ExtensionParams<ResourceTilesParameters>,
	) => (
		<Suspense fallback={<UFOLoadHold name="resource-tiles-extension-loader" />}>
			<ResourceTilesExtensionLazy
				{...defaultExtensionHandlerProps}
				extensionNode={extension}
				isInEditor={false}
			/>
		</Suspense>
	);

	return handlers;
};

const getJSMExtensionHandlers = (
	contentId: string,
	PropsProvider: ComponentType<ProductDependenciesProviderProps>,
	rovoWidgetQueryReference?: PreloadedQuery<rovoWidgetProviderQuery> | null,
): ExtensionHandlers => {
	const handlers: ExtensionHandlers = {};

	if (!contentId) {
		return handlers;
	}

	const defaultExtensionHandlerProps = {
		contentId,
		propsProvider: PropsProvider,
		mode: ExtensionMode.RENDERER,
		CustomSearchComponent: HelpObjectSearchComponent,
	};

	handlers[CALLOUT_BANNER_EXTENSION_TYPE] = (
		extension: ExtensionParams<CalloutBannerParametersType>,
	) => (
		<Suspense fallback={<UFOLoadHold name="page-topper-extension-loader" />}>
			<CalloutBannerExtensionLazy
				{...defaultExtensionHandlerProps}
				extensionNode={extension}
				rovoWidgetQueryReference={rovoWidgetQueryReference}
			/>
		</Suspense>
	);

	// JSM Help Extension Handler
	handlers[JSM_HELP_EXTENSION_TYPE] = (extension: ExtensionParams<JSMHelpParameters>) => (
		<Suspense fallback={<UFOLoadHold name="jsm-help-extension-loader" />}>
			<JSMHelpExtensionLazy {...defaultExtensionHandlerProps} extensionNode={extension} />
		</Suspense>
	);

	Object.assign(handlers, getHubForgeAndConnectExtensionHandlers(contentId, PropsProvider));

	return handlers;
};

export const useProductExtensionHandlers = (
	props: ProductExtensionHandlersProps,
): ExtensionHandlers<any> => {
	const { contentId, PropsProvider, product, rovoWidgetQueryReference } = props;

	const extensionHandlers: ExtensionHandlers = useMemo(() => {
		if (product === 'CUSTOMER_SERVICE') {
			return getCSMExtensionHandlers(contentId, PropsProvider);
		}

		// Rovo in the callout banner is only available in help hubs, so the
		// preloaded Rovo widget query is only forwarded here.
		if (product === 'HELP_HUB' && fg('enable-jsm-in-hubs')) {
			return getJSMExtensionHandlers(contentId, PropsProvider, rovoWidgetQueryReference);
		}

		return {};
	}, [product, contentId, PropsProvider, rovoWidgetQueryReference]);

	return extensionHandlers;
};
